import React, { useMemo } from 'react';
import './ExecutionCommon.css';

function ExecutionStats({ execution, history, processNodes, nodeResultsCache }) {
  // Calculate statistics from execution and history
  const stats = useMemo(() => {
    if (!execution) return null;

    // Node counts
    const completedCount = execution.completed_nodes?.length || 0;
    const failedCount = execution.failed_nodes?.length || 0;
    const skippedCount = execution.skipped_nodes?.length || 0;
    const activeCount = execution.active_nodes?.length || 0;
    const pendingCount = execution.pending_nodes?.length || 0;
    const totalNodes = processNodes?.length || 0;

    // Authoritative usage from backend (preferred over re-summing history)
    const ctx = execution.context || {};
    const authoritativeUsage = ctx.llm_usage || null;
    const ragSavings = ctx.rag_savings || null;

    // Token statistics
    let totalTokens = 0;
    let promptTokens = 0;
    let completionTokens = 0;
    let costUsd = 0;
    const nodeTokens = [];

    // Duration statistics
    let totalDuration = 0;
    const nodeDurations = [];
    let fastestNode = null;
    let slowestNode = null;

    // Process history events
    history?.forEach(event => {
      if (event.duration_ms && event.node_id && event.action === 'completed') {
        nodeDurations.push({ nodeId: event.node_id, duration: event.duration_ms });
        totalDuration += event.duration_ms;
        
        if (!fastestNode || event.duration_ms < fastestNode.duration) {
          fastestNode = { nodeId: event.node_id, duration: event.duration_ms };
        }
        if (!slowestNode || event.duration_ms > slowestNode.duration) {
          slowestNode = { nodeId: event.node_id, duration: event.duration_ms };
        }
      }
    });

    // Also check node results from on-demand cache for durations
    const resultsMap = nodeResultsCache || {};
    if (Object.keys(resultsMap).length > 0) {
      Object.entries(resultsMap).forEach(([nodeId, result]) => {
        if (result.duration_ms && !nodeDurations.find(n => n.nodeId === nodeId)) {
          nodeDurations.push({ nodeId, duration: result.duration_ms });
          totalDuration += result.duration_ms;
          
          if (!fastestNode || result.duration_ms < fastestNode.duration) {
            fastestNode = { nodeId, duration: result.duration_ms };
          }
          if (!slowestNode || result.duration_ms > slowestNode.duration) {
            slowestNode = { nodeId, duration: result.duration_ms };
          }
        }
      });
    }

    // Token usage: prefer authoritative blob from backend, fall back to summing.
    if (authoritativeUsage && authoritativeUsage.overall) {
      const ov = authoritativeUsage.overall || {};
      totalTokens = Number(ov.total_tokens || 0);
      promptTokens = Number(ov.prompt_tokens || 0);
      completionTokens = Number(ov.completion_tokens || 0);
      costUsd = Number(ov.cost_usd || 0);
      const perNode = authoritativeUsage.per_node || {};
      Object.entries(perNode).forEach(([nodeId, n]) => {
        if (n && n.total_tokens > 0) {
          nodeTokens.push({ nodeId, tokens: Number(n.total_tokens || 0) });
        }
      });
    } else {
      // Legacy fallback for older runs without context.llm_usage
      history?.forEach(event => {
        if (event.tokens_used) {
          totalTokens += event.tokens_used;
          if (event.node_id) {
            nodeTokens.push({ nodeId: event.node_id, tokens: event.tokens_used });
          }
        }
        if (event.prompt_tokens) promptTokens += event.prompt_tokens;
        if (event.completion_tokens) completionTokens += event.completion_tokens;
      });
      Object.entries(resultsMap).forEach(([nodeId, result]) => {
        if (result.tokens_used && !nodeTokens.find(n => n.nodeId === nodeId)) {
          totalTokens += result.tokens_used;
          nodeTokens.push({ nodeId, tokens: result.tokens_used });
        }
        if (result.prompt_tokens) promptTokens += result.prompt_tokens;
        if (result.completion_tokens) completionTokens += result.completion_tokens;
      });
    }

    const avgDuration = nodeDurations.length > 0 
      ? Math.round(totalDuration / nodeDurations.length) 
      : 0;

    // Gate statistics
    const waitingGatesCount = execution.waiting_gates 
      ? Object.keys(execution.waiting_gates).length 
      : 0;
    const firedGatesCount = execution.fired_gates?.length || 0;

    // Error statistics
    const errors = [];
    history?.forEach(event => {
      if (event.error && event.node_id) {
        errors.push({
          nodeId: event.node_id,
          error: event.error,
          errorType: event.error_type,
        });
      }
    });

    // Cost estimate: prefer measured cost from authoritative usage; fall back to gpt-4o-style pricing.
    const estimatedCost = (costUsd > 0
      ? costUsd
      : promptTokens * 0.0000025 + completionTokens * 0.00001
    ).toFixed(6);

    return {
      authoritativeUsage,
      ragSavings,
      // Node counts
      totalNodes,
      completedCount,
      failedCount,
      skippedCount,
      activeCount,
      pendingCount,
      progress: execution.progress || 0,
      // Tokens
      totalTokens,
      promptTokens,
      completionTokens,
      nodeTokens: nodeTokens.sort((a, b) => b.tokens - a.tokens).slice(0, 5),
      // Duration
      totalDuration,
      avgDuration,
      fastestNode,
      slowestNode,
      nodeDurations: nodeDurations.sort((a, b) => b.duration - a.duration).slice(0, 5),
      // Gates
      waitingGatesCount,
      firedGatesCount,
      // Errors
      errors,
      // Cost
      estimatedCost,
    };
  }, [execution, history, processNodes, nodeResultsCache]);

  if (!execution) {
    return (
      <div className="execution-stats empty">
        <div className="empty-state">
          <div className="empty-icon">📈</div>
          <h3>No Statistics Available</h3>
          <p>Start an execution to see statistics</p>
        </div>
      </div>
    );
  }

  // Get node name by ID
  const getNodeName = (nodeId) => {
    const node = processNodes?.find(n => n.node_id === nodeId);
    return node?.name || nodeId;
  };

  return (
    <div className="execution-stats">
      {/* Progress Overview */}
      <div className="stats-section">
        <h3>Progress Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-value">{stats.progress.toFixed(1)}%</span>
            <span className="stat-label">Complete</span>
          </div>
          <div className="stat-card completed">
            <span className="stat-value">{stats.completedCount}</span>
            <span className="stat-label">Completed</span>
          </div>
          <div className="stat-card active">
            <span className="stat-value">{stats.activeCount}</span>
            <span className="stat-label">Active</span>
          </div>
          <div className="stat-card pending">
            <span className="stat-value">{stats.pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-card failed">
            <span className="stat-value">{stats.failedCount}</span>
            <span className="stat-label">Failed</span>
          </div>
          <div className="stat-card skipped">
            <span className="stat-value">{stats.skippedCount}</span>
            <span className="stat-label">Skipped</span>
          </div>
        </div>

        {/* Visual Progress Bar */}
        <div className="progress-breakdown">
          <div className="progress-bar-segmented">
            <div 
              className="segment completed" 
              style={{ width: `${(stats.completedCount / stats.totalNodes) * 100}%` }}
              title={`Completed: ${stats.completedCount}`}
            />
            <div 
              className="segment active" 
              style={{ width: `${(stats.activeCount / stats.totalNodes) * 100}%` }}
              title={`Active: ${stats.activeCount}`}
            />
            <div 
              className="segment failed" 
              style={{ width: `${(stats.failedCount / stats.totalNodes) * 100}%` }}
              title={`Failed: ${stats.failedCount}`}
            />
            <div 
              className="segment skipped" 
              style={{ width: `${(stats.skippedCount / stats.totalNodes) * 100}%` }}
              title={`Skipped: ${stats.skippedCount}`}
            />
          </div>
          <div className="progress-legend">
            <span className="legend-item completed">● Completed</span>
            <span className="legend-item active">● Active</span>
            <span className="legend-item failed">● Failed</span>
            <span className="legend-item skipped">● Skipped</span>
            <span className="legend-item pending">● Pending</span>
          </div>
        </div>
      </div>

      {/* Timing Statistics */}
      <div className="stats-section">
        <h3>Timing</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-value">{stats.totalDuration}ms</span>
            <span className="stat-label">Total Duration</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.avgDuration}ms</span>
            <span className="stat-label">Avg per Node</span>
          </div>
          {stats.fastestNode && (
            <div className="stat-card">
              <span className="stat-value">{stats.fastestNode.duration}ms</span>
              <span className="stat-label">Fastest</span>
              <span className="stat-detail">{getNodeName(stats.fastestNode.nodeId)}</span>
            </div>
          )}
          {stats.slowestNode && (
            <div className="stat-card">
              <span className="stat-value">{stats.slowestNode.duration}ms</span>
              <span className="stat-label">Slowest</span>
              <span className="stat-detail">{getNodeName(stats.slowestNode.nodeId)}</span>
            </div>
          )}
        </div>

        {/* Duration Distribution */}
        {stats.nodeDurations.length > 0 && (
          <div className="distribution-chart">
            <h4>Top 5 Nodes by Duration</h4>
            <div className="bar-chart">
              {stats.nodeDurations.map((item, idx) => (
                <div key={idx} className="bar-row">
                  <span className="bar-label">{getNodeName(item.nodeId)}</span>
                  <div className="bar-container">
                    <div 
                      className="bar" 
                      style={{ 
                        width: `${(item.duration / stats.slowestNode.duration) * 100}%` 
                      }}
                    />
                  </div>
                  <span className="bar-value">{item.duration}ms</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Token Statistics */}
      <div className="stats-section">
        <h3>Token Usage</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-value">{stats.totalTokens.toLocaleString()}</span>
            <span className="stat-label">Total Tokens</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.promptTokens.toLocaleString()}</span>
            <span className="stat-label">Prompt Tokens</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.completionTokens.toLocaleString()}</span>
            <span className="stat-label">Completion Tokens</span>
          </div>
          <div className="stat-card cost">
            <span className="stat-value">${stats.estimatedCost}</span>
            <span className="stat-label">{stats.authoritativeUsage ? 'LLM Cost' : 'Est. Cost'}</span>
          </div>
        </div>

        {/* Per-phase breakdown when authoritative usage is available */}
        {stats.authoritativeUsage && stats.authoritativeUsage.per_phase &&
         Object.keys(stats.authoritativeUsage.per_phase).length > 0 && (
          <div className="distribution-chart">
            <h4>Token Usage by Phase</h4>
            <table className="phase-usage-table" style={{width: '100%', fontSize: '13px', borderCollapse: 'collapse'}}>
              <thead>
                <tr>
                  <th style={{textAlign:'left', padding: '4px 8px'}}>Phase</th>
                  <th style={{textAlign:'right', padding: '4px 8px'}}>Calls</th>
                  <th style={{textAlign:'right', padding: '4px 8px'}}>Prompt</th>
                  <th style={{textAlign:'right', padding: '4px 8px'}}>Completion</th>
                  <th style={{textAlign:'right', padding: '4px 8px'}}>Total</th>
                  <th style={{textAlign:'right', padding: '4px 8px'}}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(stats.authoritativeUsage.per_phase)
                  .sort((a, b) => (b.total_tokens || 0) - (a.total_tokens || 0))
                  .map((p) => (
                    <tr key={p.phase}>
                      <td style={{padding: '4px 8px'}}>{p.phase || '—'}</td>
                      <td style={{textAlign:'right', padding: '4px 8px'}}>{(p.call_count || 0).toLocaleString()}</td>
                      <td style={{textAlign:'right', padding: '4px 8px'}}>{(p.prompt_tokens || 0).toLocaleString()}</td>
                      <td style={{textAlign:'right', padding: '4px 8px'}}>{(p.completion_tokens || 0).toLocaleString()}</td>
                      <td style={{textAlign:'right', padding: '4px 8px'}}><strong>{(p.total_tokens || 0).toLocaleString()}</strong></td>
                      <td style={{textAlign:'right', padding: '4px 8px'}}>${(p.cost_usd || 0).toFixed(5)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Token Distribution */}
        {stats.nodeTokens.length > 0 && (
          <div className="distribution-chart">
            <h4>Top 5 Nodes by Token Usage</h4>
            <div className="bar-chart">
              {stats.nodeTokens.map((item, idx) => (
                <div key={idx} className="bar-row">
                  <span className="bar-label">{getNodeName(item.nodeId)}</span>
                  <div className="bar-container">
                    <div 
                      className="bar tokens" 
                      style={{ 
                        width: `${(item.tokens / stats.nodeTokens[0].tokens) * 100}%` 
                      }}
                    />
                  </div>
                  <span className="bar-value">{item.tokens}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RAG Comparison */}
      {stats.ragSavings && stats.ragSavings.tiers && (() => {
        const tiers = stats.ragSavings.tiers || {};
        const headline = stats.ragSavings.headline || {};
        const actual = stats.ragSavings.actual || {};
        const fmtNum = (n) => Number(n || 0).toLocaleString();
        const fmtCost = (c) => {
          const v = Number(c || 0);
          if (v <= 0) return '—';
          if (v < 0.0001) return '<$0.0001';
          if (v < 0.01) return `$${v.toFixed(5)}`;
          return `$${v.toFixed(4)}`;
        };
        const fmtPct = (p) => `${Number(p || 0).toFixed(1)}%`;
        const tierMeta = [
          { key: 'conservative', label: 'Conservative', tip: 'k=15 chunks, single LLM pass' },
          { key: 'realistic', label: 'Realistic', tip: 'k=25 chunks, single LLM pass' },
          { key: 'quality_matched', label: 'Quality-Matched (Headline)', tip: 'k=25 with re-ranking + multi-pass (1.5x)' },
          { key: 'naive_full_doc', label: 'Full-Document', tip: 'Entire document per query (compliance-safe RAG)' },
        ];
        return (
          <div className="stats-section">
            <h3>vs Traditional RAG</h3>
            <div className="stats-grid">
              <div className="stat-card cost">
                <span className="stat-value">{fmtPct(headline.savings_pct)}</span>
                <span className="stat-label">Saved vs Quality-Matched RAG</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{fmtCost(headline.savings_cost_usd)}</span>
                <span className="stat-label">Cost Saved</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{fmtNum(actual.total_tokens)}</span>
                <span className="stat-label">Actual Tokens</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{fmtCost(actual.cost_usd)}</span>
                <span className="stat-label">Actual Cost</span>
              </div>
            </div>
            <div className="distribution-chart">
              <h4>Tier Breakdown</h4>
              <table style={{width: '100%', fontSize: '13px', borderCollapse: 'collapse'}}>
                <thead>
                  <tr>
                    <th style={{textAlign:'left', padding: '4px 8px'}}>Tier</th>
                    <th style={{textAlign:'right', padding: '4px 8px'}}>RAG Tokens</th>
                    <th style={{textAlign:'right', padding: '4px 8px'}}>RAG Cost</th>
                    <th style={{textAlign:'right', padding: '4px 8px'}}>Saved Tokens</th>
                    <th style={{textAlign:'right', padding: '4px 8px'}}>% Saved</th>
                  </tr>
                </thead>
                <tbody>
                  {tierMeta.map(({ key, label, tip }) => {
                    const t = tiers[key];
                    if (!t) return null;
                    return (
                      <tr key={key} title={tip}>
                        <td style={{padding: '4px 8px'}}><strong>{label}</strong></td>
                        <td style={{textAlign:'right', padding: '4px 8px'}}>{fmtNum(t.rag_only_total_tokens || t.total_tokens)}</td>
                        <td style={{textAlign:'right', padding: '4px 8px'}}>{fmtCost(t.cost_usd)}</td>
                        <td style={{textAlign:'right', padding: '4px 8px'}}>{fmtNum(t.savings_tokens_vs_actual)}</td>
                        <td style={{textAlign:'right', padding: '4px 8px'}}><strong>{fmtPct(t.savings_pct_vs_actual)}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Gate Statistics */}
      {(stats.waitingGatesCount > 0 || stats.firedGatesCount > 0) && (
        <div className="stats-section">
          <h3>Gates</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-value">{stats.firedGatesCount}</span>
              <span className="stat-label">Gates Selected</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.waitingGatesCount}</span>
              <span className="stat-label">Gates Waiting</span>
            </div>
          </div>
        </div>
      )}

      {/* Error Summary */}
      {stats.errors.length > 0 && (
        <div className="stats-section errors">
          <h3>Errors ({stats.errors.length})</h3>
          <div className="errors-list">
            {stats.errors.map((err, idx) => (
              <div key={idx} className="error-item">
                <div className="error-header">
                  <span className="error-node">{getNodeName(err.nodeId)}</span>
                  {err.errorType && (
                    <span className="error-type">{err.errorType}</span>
                  )}
                </div>
                <div className="error-message">{err.error}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExecutionStats;
